import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  DEFAULT_OFFLINE_METHOD,
  OFFLINE_PAYMENT_METHODS,
  paymentMethodLabelKey,
} from './payment-methods';
import { formatCurrency, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useRecordPayment } from './events.api';
/** The fields this dialog reads — booking and membership purchases both have them. */
export interface PayablePurchase {
  id: string;
  createdAt: string | null;
  amount: number | null;
  amountPaid: number | null;
}

/** The sentinel the original used for "open a new payment request". */
const NEW_PURCHASE = 'NEW';

/**
 * Record an offline payment against a booking.
 *
 * When the booking has open payment requests the payment can be applied to one
 * of them or to a new one; when it has none that choice is omitted entirely and
 * the server decides, which is what the original did by only sending the field
 * if there was something to choose between.
 */
export function RecordPaymentDialog({
  targetId,
  purchases,
  open,
  onOpenChange,
  onSaved,
  endpoint = 'bookings',
  seedAmount,
  renderEcho,
}: {
  targetId: string | null;
  purchases: PayablePurchase[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  endpoint?: 'bookings' | 'memberships';
  /**
   * What is still owed, when the caller can work it out. A subscription carries
   * no running balance, so its page derives this from the plan's list price
   * less whatever has been paid so far.
   */
  seedAmount?: number | null;
  /** A sentence restating what is about to be recorded, in the caller's terms. */
  renderEcho?: (payment: { amount: number; method: number }) => ReactNode;
}) {
  const { t } = useTranslation();
  const record = useRecordPayment(endpoint);

  const [amount, setAmount] = useState(0);
  // Cash: the overwhelming majority of hand-recorded payments.
  const [method, setMethod] = useState(String(DEFAULT_OFFLINE_METHOD));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [appliedTo, setAppliedTo] = useState(NEW_PURCHASE);
  // Hydrate once per target; the dialog stays mounted between openings.
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (targetId && seededFor !== targetId) {
    setSeededFor(targetId);
    setAmount(seedAmount && seedAmount > 0 ? seedAmount : 0);
    setReference('');
    setNotes('');
    setAppliedTo(NEW_PURCHASE);
  }

  if (!targetId) return null;

  const options = purchases.filter(
    (purchase) => (purchase.amount ?? 0) > (purchase.amountPaid ?? 0)
  );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('events.recordPayment')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('events.recordPaymentBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          {options.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                {t('events.applyPaymentTo')}
              </Label>
              <div className="space-y-1">
                {options.map((purchase) => (
                  <button
                    key={purchase.id}
                    type="button"
                    onClick={() => setAppliedTo(purchase.id)}
                    className={cn(
                      'hover:bg-accent w-full rounded-lg border p-2 text-left text-sm transition-colors',
                      appliedTo === purchase.id && 'border-primary bg-accent'
                    )}
                  >
                    <span className="tabular-nums">
                      {formatCurrency(purchase.amount)}
                    </span>
                    <span className="text-muted-foreground ml-2 text-xs">
                      {formatDateTime(purchase.createdAt)}
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setAppliedTo(NEW_PURCHASE)}
                  className={cn(
                    'hover:bg-accent w-full rounded-lg border p-2 text-left text-sm transition-colors',
                    appliedTo === NEW_PURCHASE && 'border-primary bg-accent'
                  )}
                >
                  {t('events.newPaymentOption')}
                </button>
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                {t('events.amount')}
              </Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                {t('events.method')}
              </Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Only methods at or above 10 were collected outside the
                      platform, so only those can be recorded by hand. */}
                  {OFFLINE_PAYMENT_METHODS.map((value) => (
                    <SelectItem key={value} value={String(value)}>
                      {t(paymentMethodLabelKey(value))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('events.reference')}
            </Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('events.adminNotes')}
            </Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {renderEcho && amount > 0 && (
            <div className="bg-accent text-accent-foreground rounded-xl border p-3 text-sm">
              {renderEcho({ amount, method: Number(method) })}
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={record.isPending || amount <= 0}
            onClick={() =>
              record.mutate(
                {
                  targetId,
                  amount,
                  method: Number(method),
                  offlineReference: reference,
                  adminNotes: notes,
                  // Silent when there was nothing to choose between.
                  ...(options.length > 0
                    ? appliedTo === NEW_PURCHASE
                      ? { createNewPurchase: true }
                      : { purchaseId: appliedTo }
                    : {}),
                },
                {
                  onSuccess: () => {
                    toast.success(t('events.paymentRecorded'));
                    onOpenChange(false);
                    onSaved();
                  },
                  onError: (error) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t('events.bookingActionFailed')
                    ),
                }
              )
            }
          >
            {record.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              t('events.recordPayment')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
